import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import AcceptTermsModal from "@/components/AcceptTermsModal";
import FeedbackButton from "@/components/FeedbackButton";
import ChatWidget from "@/components/ChatWidget";
import ProfileMenu from "@/components/ProfileMenu";
import AlertWatcher from "@/components/AlertWatcher";
import { NavLink } from "react-router-dom";
import { Home, Car, Gamepad2, Trophy, Crown, Hammer, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const mobileLinks = [
  { to: "/dashboard", label: "Hub", icon: Home, testid: "nav-dashboard-mobile" },
  { to: "/garage", label: "Garage", icon: Car, testid: "nav-garage-mobile" },
  { to: "/lobbies", label: "Race", icon: Gamepad2, testid: "nav-lobbies-mobile" },
  { to: "/courses", label: "Build", icon: Hammer, testid: "nav-courses-mobile" },
  { to: "/bosses", label: "Bosses", icon: Crown, testid: "nav-bosses-mobile" },
  { to: "/leaderboard", label: "Rank", icon: Trophy, testid: "nav-leaderboard-mobile" },
];

export default function AppLayout({ children, className = "" }) {
  // null = unknown, false = current, true = needs to accept the new version
  const [needsAcceptance, setNeedsAcceptance] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/legal/me`, { withCredentials: true });
        if (!cancelled) setNeedsAcceptance(Boolean(r.data?.needs_acceptance));
      } catch (_e) {
        // If the call fails (network/auth) treat as accepted to avoid
        // false-positive blocking — re-checked on next page load anyway.
        if (!cancelled) setNeedsAcceptance(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex min-h-screen bg-[#0A0A0A]">
      <Sidebar />
      <main className={`flex-1 min-w-0 pb-20 lg:pb-0 ${className}`}>
        {user?.is_suspended && (
          <div
            className="bg-[#FF3B30]/15 border-y-2 border-[#FF3B30] px-4 py-3 flex items-center gap-3"
            data-testid="suspension-banner"
          >
            <Ban className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm uppercase tracking-tight text-[#FF3B30]">
                Account suspended
              </div>
              {user.suspended_reason && (
                <div className="text-xs text-white/70 mt-0.5">{user.suspended_reason}</div>
              )}
              <div className="text-[11px] text-white/40 mt-1">
                You can read messages from admins but cannot create or join races.
              </div>
            </div>
          </div>
        )}
        {children}
      </main>
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-black/80 backdrop-blur-xl"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex">
          {mobileLinks.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-3 text-[10px] uppercase tracking-widest ${
                  isActive ? "text-[#FF3B30]" : "text-white/60"
                }`
              }
            >
              <Icon className="w-5 h-5 mb-1" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
      {needsAcceptance && (
        <AcceptTermsModal onAccepted={() => setNeedsAcceptance(false)} />
      )}
      <FeedbackButton />
      <ProfileMenu />
      <ChatWidget />
      <AlertWatcher />
    </div>
  );
}
